import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Estimate } from '../types';
import { PDFTemplateService } from './pdfTemplateService';
import { feesFromStored } from '../utils/estimateTotals';

export interface CompanyInfo {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  website?: string;
  logo?: string; // base64 or URL
}

export interface PDFLayoutOptions {
  layout: 'professional' | 'compact';
  includeCompanyLogo: boolean;
  showInternalNotes: boolean;
  showPartDetails: boolean;
  showDiscountBreakdown: boolean;
  highlightDiscounts: boolean;
  showOriginalPrices: boolean;
  colorScheme: 'blue' | 'green' | 'gray' | 'red' | 'purple' | 'orange';
  fontFamily: 'helvetica' | 'times' | 'courier';
  fontSize: 'small' | 'medium' | 'large';
  spacing: 'tight' | 'normal' | 'loose';
  borderStyle: 'none' | 'simple' | 'rounded';
  showPageNumbers: boolean;
}

export class PDFService {
  private static readonly DEFAULT_COMPANY_INFO: CompanyInfo = {
    name: 'CNF Auto LLC',
    address: '123 Main Street',
    city: 'City',
    state: 'State',
    zip: '12345',
    phone: '(555) 123-4567',
    email: 'info@cnfautosuite.com',
    website: 'www.cnfautosuite.com',
  };

  // Utility functions for consistent number formatting
  private static formatCurrency(amount: number): string {
    return `$${(Math.round(amount * 100) / 100).toFixed(2)}`;
  }

  private static formatPercentage(percent: number): string {
    return `${(Math.round(percent * 100) / 100).toFixed(2)}%`;
  }

  private static checkPageBreak(pdf: jsPDF, yPosition: number, requiredSpace: number): number {
    const pageHeight = pdf.internal.pageSize.getHeight();
    const footerSpace = 30; // Reserve space for footer

    if (yPosition + requiredSpace + footerSpace > pageHeight) {
      pdf.addPage();
      return 30; // Return new starting position
    }
    return yPosition;
  }

  private static readonly COLOR_SCHEMES = {
    blue: { primary: '#1890ff', secondary: '#f0f8ff', text: '#262626' },
    green: { primary: '#52c41a', secondary: '#f6ffed', text: '#262626' },
    gray: { primary: '#595959', secondary: '#fafafa', text: '#262626' },
    red: { primary: '#ff4d4f', secondary: '#fff2f0', text: '#262626' },
    purple: { primary: '#722ed1', secondary: '#f9f0ff', text: '#262626' },
    orange: { primary: '#fa8c16', secondary: '#fff7e6', text: '#262626' },
  };

  private static readonly FONT_SETTINGS = {
    small: { title: 20, header: 12, body: 9, footer: 7 },
    medium: { title: 24, header: 14, body: 10, footer: 8 },
    large: { title: 28, header: 16, body: 12, footer: 9 },
  };

  private static readonly SPACING_SETTINGS = {
    tight: { lineHeight: 1.2, sectionGap: 8, itemGap: 5 },
    normal: { lineHeight: 1.4, sectionGap: 12, itemGap: 8 },
    loose: { lineHeight: 1.6, sectionGap: 16, itemGap: 10 },
  };

  static async generateEstimatePDF(
    estimate: Estimate,
    options?: Partial<PDFLayoutOptions>,
    companyInfo?: Partial<CompanyInfo>
  ): Promise<void> {
    // Use default settings with any provided overrides
    const finalOptions = PDFTemplateService.getSettingsWithOverrides(options);
    const finalCompanyInfo = PDFTemplateService.getCompanyInfoWithOverrides(companyInfo);
    const pdf = await this.createPDF(estimate, finalOptions, finalCompanyInfo);

    // Save the PDF
    pdf.save(`estimate-${estimate.estimate_number}.pdf`);
  }

  static async generateEstimatePDFBuffer(
    estimate: Estimate,
    options?: Partial<PDFLayoutOptions>,
    companyInfo?: Partial<CompanyInfo>
  ): Promise<string> {
    // Use default settings with any provided overrides
    const finalOptions = PDFTemplateService.getSettingsWithOverrides(options);
    const finalCompanyInfo = PDFTemplateService.getCompanyInfoWithOverrides(companyInfo);
    const pdf = await this.createPDF(estimate, finalOptions, finalCompanyInfo);

    // Return base64 encoded PDF for email
    return pdf.output('datauristring').split(',')[1];
  }

  private static async createPDF(
    estimate: Estimate,
    options: PDFLayoutOptions,
    companyInfo: CompanyInfo
  ): Promise<jsPDF> {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const colors = this.COLOR_SCHEMES[options.colorScheme];
    const fonts = this.FONT_SETTINGS[options.fontSize];
    const spacing = this.SPACING_SETTINGS[options.spacing];

    // Set default font family
    pdf.setFont(options.fontFamily, 'normal');

    if (options.layout === 'professional') {
      await this.generateProfessionalLayout(pdf, estimate, options, companyInfo, colors);
    } else {
      await this.generateCompactLayout(pdf, estimate, options, companyInfo, colors);
    }

    return pdf;
  }

  private static async generateProfessionalLayout(
    pdf: jsPDF,
    estimate: Estimate,
    options: PDFLayoutOptions,
    companyInfo: CompanyInfo,
    colors: any
  ): Promise<void> {
    const fonts = this.FONT_SETTINGS[options.fontSize];
    const spacing = this.SPACING_SETTINGS[options.spacing];
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPosition = 20;

    // Header with company info and logo
    yPosition = this.addHeader(pdf, companyInfo, options.includeCompanyLogo, colors, yPosition);

    // Title
    pdf.setFontSize(fonts.title);
    pdf.setTextColor(colors.primary);
    pdf.text('ESTIMATE', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += spacing.sectionGap + 7;

    // Estimate details
    yPosition = this.addEstimateDetails(pdf, estimate, colors, yPosition, fonts);

    // Customer and vehicle info side by side
    yPosition = this.addCustomerVehicleInfo(pdf, estimate, colors, yPosition, pageWidth, fonts);

    // Line items table
    yPosition = this.addLineItemsTable(pdf, estimate, options, colors, yPosition, pageWidth, fonts);

    // Total section
    yPosition = this.addTotalSection(pdf, estimate, colors, yPosition, pageWidth);

    // Internal notes (if enabled)
    if (options.showInternalNotes && estimate.internal_notes) {
      yPosition = this.addInternalNotes(pdf, estimate, colors, yPosition);
    }

    // Add disclaimer in document flow (with automatic page break if needed)
    if (estimate.disclaimer) {
      yPosition = this.addDisclaimer(pdf, estimate, colors, yPosition, pageWidth);
    }

    // Footer - positioned at bottom of last page
    this.addFooter(pdf, companyInfo, colors, options.showPageNumbers);
  }

  private static async generateCompactLayout(
    pdf: jsPDF,
    estimate: Estimate,
    options: PDFLayoutOptions,
    companyInfo: CompanyInfo,
    colors: any
  ): Promise<void> {
    const pageWidth = pdf.internal.pageSize.getWidth();
    let yPosition = 15;

    // Compact header
    yPosition = this.addCompactHeader(pdf, companyInfo, estimate, colors, yPosition, pageWidth);

    // Combined customer/vehicle section
    yPosition = this.addCompactCustomerVehicle(pdf, estimate, colors, yPosition);

    // Compact line items
    yPosition = this.addCompactLineItems(pdf, estimate, options, colors, yPosition, pageWidth);

    // Total
    yPosition = this.addCompactTotal(pdf, estimate, colors, yPosition, pageWidth);

    // Disclaimer in document flow (with automatic page break if needed)
    if (estimate.disclaimer) {
      yPosition = this.addDisclaimer(pdf, estimate, colors, yPosition, pageWidth);
    }
  }

  private static addHeader(
    pdf: jsPDF,
    companyInfo: CompanyInfo,
    includeLogo: boolean,
    colors: any,
    yPosition: number
  ): number {
    const pageWidth = pdf.internal.pageSize.getWidth();

    // Company name
    pdf.setFontSize(20);
    pdf.setTextColor(colors.primary);
    pdf.text(companyInfo.name, 20, yPosition);

    // Company details
    pdf.setFontSize(10);
    pdf.setTextColor(colors.text);
    yPosition += 8;
    pdf.text(companyInfo.address, 20, yPosition);
    yPosition += 4;
    pdf.text(`${companyInfo.city}, ${companyInfo.state} ${companyInfo.zip}`, 20, yPosition);
    yPosition += 4;
    pdf.text(`Phone: ${companyInfo.phone}`, 20, yPosition);
    yPosition += 4;
    pdf.text(`Email: ${companyInfo.email}`, 20, yPosition);

    if (companyInfo.website) {
      yPosition += 4;
      pdf.text(`Web: ${companyInfo.website}`, 20, yPosition);
    }

    // Logo placeholder or actual logo (if enabled)
    if (includeLogo) {
      if (companyInfo.logo) {
        // If logo is provided, add it to the PDF
        try {
          pdf.addImage(companyInfo.logo, 'JPEG', pageWidth - 60, 20, 40, 30);
        } catch (error) {
          // Fallback to placeholder if logo fails to load
          pdf.setDrawColor(colors.primary);
          pdf.rect(pageWidth - 60, 20, 40, 30);
          pdf.setFontSize(8);
          pdf.text('LOGO', pageWidth - 40, 37, { align: 'center' });
        }
      } else {
        // Logo placeholder
        pdf.setDrawColor(colors.primary);
        pdf.rect(pageWidth - 60, 20, 40, 30);
        pdf.setFontSize(8);
        pdf.text('LOGO', pageWidth - 40, 37, { align: 'center' });
      }
    }

    return yPosition + 15;
  }

  private static addEstimateDetails(
    pdf: jsPDF,
    estimate: Estimate,
    colors: any,
    yPosition: number,
    fonts: any
  ): number {
    const pageWidth = pdf.internal.pageSize.getWidth();

    // Estimate details box
    pdf.setDrawColor(colors.primary);
    pdf.setFillColor(colors.secondary);
    pdf.rect(20, yPosition, pageWidth - 40, 25, 'F');
    pdf.rect(20, yPosition, pageWidth - 40, 25);

    pdf.setFontSize(fonts.header);
    pdf.setTextColor(colors.text);

    // Left column
    pdf.text('Estimate #:', 25, yPosition + 8);
    pdf.setFont(undefined, 'bold');
    pdf.text(estimate.estimate_number, 55, yPosition + 8);

    pdf.setFont(undefined, 'normal');
    pdf.text('Date:', 25, yPosition + 16);
    pdf.text(new Date(estimate.created_at || '').toLocaleDateString(), 55, yPosition + 16);

    // Right column
    pdf.text('Status:', pageWidth - 80, yPosition + 8);
    pdf.setFont(undefined, 'bold');
    pdf.text(estimate.status.toUpperCase(), pageWidth - 50, yPosition + 8);

    pdf.setFont(undefined, 'normal');
    if (estimate.valid_until) {
      pdf.text('Valid Until:', pageWidth - 80, yPosition + 16);
      pdf.text(new Date(estimate.valid_until).toLocaleDateString(), pageWidth - 50, yPosition + 16);
    }

    return yPosition + 35;
  }

  private static addCustomerVehicleInfo(
    pdf: jsPDF,
    estimate: Estimate,
    colors: any,
    yPosition: number,
    pageWidth: number,
    fonts: any
  ): number {
    const columnWidth = (pageWidth - 50) / 2;
    const startYPosition = yPosition; // Store initial position for vehicle alignment

    // Customer info
    pdf.setFontSize(fonts.header);
    pdf.setTextColor(colors.primary);
    pdf.text('CUSTOMER', 20, yPosition);

    pdf.setFontSize(fonts.body);
    pdf.setTextColor(colors.text);
    yPosition += 8;

    if (estimate.customer) {
      const customerName = estimate.customer.company_name ||
        `${estimate.customer.first_name} ${estimate.customer.last_name}`;
      pdf.text(customerName, 20, yPosition);
      yPosition += 5;

      if (estimate.customer.address) {
        pdf.text(estimate.customer.address, 20, yPosition);
        yPosition += 5;
        pdf.text(`${estimate.customer.city}, ${estimate.customer.state} ${estimate.customer.zip}`, 20, yPosition);
        yPosition += 5;
      }

      pdf.text(`Phone: ${estimate.customer.phone}`, 20, yPosition);
      yPosition += 5;
      pdf.text(`Email: ${estimate.customer.email}`, 20, yPosition);
    }

    // Vehicle info (right column) - aligned with customer header
    let vehicleYPosition = startYPosition; // Start at same level as customer header

    pdf.setFontSize(fonts.header);
    pdf.setTextColor(colors.primary);
    pdf.text('VEHICLE', pageWidth / 2 + 10, vehicleYPosition);

    pdf.setFontSize(fonts.body);
    pdf.setTextColor(colors.text);
    vehicleYPosition += 8;

    if (estimate.vehicle) {
      pdf.text(`${estimate.vehicle.year} ${estimate.vehicle.make} ${estimate.vehicle.model}`,
        pageWidth / 2 + 10, vehicleYPosition);
      vehicleYPosition += 5;

      pdf.text(`VIN: ${estimate.vehicle.vin}`, pageWidth / 2 + 10, vehicleYPosition);
      vehicleYPosition += 5;

      if (estimate.vehicle.license_plate) {
        pdf.text(`License: ${estimate.vehicle.license_plate}`, pageWidth / 2 + 10, vehicleYPosition);
        vehicleYPosition += 5;
      }

      if (estimate.vehicle.current_mileage) {
        pdf.text(`Mileage: ${estimate.vehicle.current_mileage.toLocaleString()}`,
          pageWidth / 2 + 10, vehicleYPosition);
        vehicleYPosition += 5;
      }
    }

    return Math.max(yPosition, vehicleYPosition) + 10;
  }

  private static addLineItemsTable(
    pdf: jsPDF,
    estimate: Estimate,
    options: PDFLayoutOptions,
    colors: any,
    yPosition: number,
    pageWidth: number,
    fonts: any
  ): number {
    // Professional section header
    pdf.setFontSize(fonts.header);
    pdf.setTextColor(colors.primary);
    pdf.text('SERVICES & PARTS', 20, yPosition);
    yPosition += 15;

    // Check if any line items have discounts to determine table structure
    const hasAnyLineItemDiscounts = estimate.line_items.some(item =>
      (item.labor_discount_percentage || item.labor_discount_amount) ||
      (item.parts && item.parts.some(part => part.discount_percentage || part.discount_amount))
    );

    // Define clean table structure - conditional discount column
    const tableHeaders = hasAnyLineItemDiscounts ?
      ['Description', 'Qty', 'Rate/Price', 'Discount', 'Amount'] :
      ['Description', 'Qty', 'Rate/Price', 'Amount'];
    const colWidths = hasAnyLineItemDiscounts ?
      [70, 15, 43, 22, 30] :
      [90, 20, 40, 30];
    const tableWidth = colWidths.reduce((sum, width) => sum + width, 0);
    const startX = 20;

    // Professional table header
    pdf.setFillColor(colors.primary);
    pdf.rect(startX, yPosition, tableWidth, 10, 'F');

    if (options.borderStyle !== 'none') {
      pdf.setDrawColor(colors.primary);
      pdf.setLineWidth(0.5);
      pdf.rect(startX, yPosition, tableWidth, 10);
    }

    // Header text
    pdf.setFontSize(fonts.body);
    pdf.setTextColor(255, 255, 255);
    pdf.setFont(undefined, 'bold');

    let xPos = startX + 3;
    tableHeaders.forEach((header, index) => {
      const textAlign = index === 0 ? 'left' : index === tableHeaders.length - 1 ? 'right' : 'center';
      const textX = textAlign === 'right' ? xPos + colWidths[index] - 3 :
                   textAlign === 'center' ? xPos + colWidths[index] / 2 : xPos;

      pdf.text(header, textX, yPosition + 7, { align: textAlign });
      xPos += colWidths[index];
    });

    yPosition += 10;
    pdf.setFont(undefined, 'normal');

    // Table rows with dynamic height based on content
    estimate.line_items.forEach((item, index) => {
      // Calculate minimum row height needed
      const minRowHeight = 18;
      const hasLaborDiscount = item.labor_discount_percentage || item.labor_discount_amount;
      const hasPartsDiscounts = item.parts && item.parts.some(part => part.discount_percentage || part.discount_amount);

      // Estimate space needed including parts breakdown if shown
      let estimatedSpace = minRowHeight;
      if (options.showPartDetails && item.parts && item.parts.length > 0) {
        estimatedSpace += 10 + (item.parts.length * 7); // Parts table header + rows
      }

      // Check if page break is needed
      yPosition = this.checkPageBreak(pdf, yPosition, estimatedSpace);

      // Calculate dynamic row height based on content
      let contentLines = 1; // Base line for description

      // Count labor discount lines
      if (hasLaborDiscount && options.showDiscountBreakdown && item.labor_hours && item.hourly_rate) {
        contentLines += 2; // Discount line + Final line
      }

      // Count parts discount lines
      if (hasPartsDiscounts && options.showDiscountBreakdown && item.parts && item.parts.length > 0) {
        contentLines += 2; // Discount line + Final line
      }

      // Calculate row height: base height + extra lines
      const baseRowHeight = 18;
      const extraLineHeight = 3;
      const rowHeight = Math.max(baseRowHeight, baseRowHeight + (contentLines - 1) * extraLineHeight);

      const currentRowY = yPosition;

      // Alternating row background
      if (index % 2 === 1) {
        pdf.setFillColor(250, 250, 250);
        pdf.rect(startX, yPosition, tableWidth, rowHeight, 'F');
      }

      // Row borders
      if (options.borderStyle !== 'none') {
        pdf.setDrawColor(220, 220, 220);
        pdf.setLineWidth(0.2);
        pdf.rect(startX, yPosition, tableWidth, rowHeight);

        // Column separators
        let sepX = startX;
        colWidths.forEach((width, idx) => {
          if (idx < colWidths.length - 1) {
            sepX += width;
            pdf.line(sepX, yPosition, sepX, yPosition + rowHeight);
          }
        });
      }

      // Column 1: Description
      pdf.setFontSize(fonts.body);
      pdf.setTextColor(colors.text);
      pdf.setFont(undefined, 'bold');

      // Split description text to fit column width
      const descLines = pdf.splitTextToSize(item.description, colWidths[0] - 6);
      let descY = yPosition + 7;

      // Display wrapped description lines (limit to 3 lines to prevent overflow)
      const maxDescLines = Math.min(descLines.length, 3);
      for (let i = 0; i < maxDescLines; i++) {
        pdf.text(descLines[i], startX + 3, descY);
        descY += 4;
      }

      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(120, 120, 120);
      pdf.text(`[${item.category}]`, startX + 3, yPosition + Math.max(12, descY - yPosition + 2));

      // Column 2: Quantity (centered vertically in the row)
      pdf.setFontSize(fonts.body);
      pdf.setTextColor(colors.text);
      const qtyY = yPosition + (rowHeight / 2) + 2; // Center vertically
      pdf.text(item.quantity.toString(), startX + colWidths[0] + colWidths[1]/2, qtyY, { align: 'center' });

      // Column 3: Rate/Price (Labor and Parts breakdown)
      const rateX = startX + colWidths[0] + colWidths[1] + 3;
      let rateY = yPosition + 6;

      // Labor rate
      if (item.labor_hours && item.hourly_rate) {
        pdf.setFontSize(9);
        pdf.setTextColor(colors.text);

        const hasLaborDiscount = item.labor_discount_percentage || item.labor_discount_amount;

        if (hasLaborDiscount && options.showDiscountBreakdown) {
          // Show original labor cost
          const originalLaborCost = item.labor_hours * item.hourly_rate;
          const laborDiscountAmount = item.labor_discount_amount ||
            (originalLaborCost * (item.labor_discount_percentage || 0) / 100);
          const discountedLaborCost = originalLaborCost - laborDiscountAmount;

          pdf.text(`Labor: ${item.labor_hours}h @ ${this.formatCurrency(item.hourly_rate)}/hr`, rateX, rateY);
          rateY += 3;

          // Show discount info
          pdf.setFontSize(8);
          pdf.setTextColor(200, 0, 0);
          if (item.labor_discount_percentage) {
            pdf.text(`  Discount: ${this.formatPercentage(item.labor_discount_percentage)} (-${this.formatCurrency(laborDiscountAmount)})`, rateX, rateY);
          } else if (item.labor_discount_amount) {
            pdf.text(`  Discount: -${this.formatCurrency(item.labor_discount_amount)}`, rateX, rateY);
          }
          rateY += 3;

          // Show final labor cost
          pdf.setFontSize(9);
          pdf.setTextColor(0, 150, 0);
          pdf.text(`  Final: ${this.formatCurrency(discountedLaborCost)}`, rateX, rateY);
          pdf.setTextColor(colors.text);
        } else {
          pdf.text(`Labor: ${item.labor_hours}h @ ${this.formatCurrency(item.hourly_rate)}/hr`, rateX, rateY);
        }
        rateY += 4;
      }

      // Parts info - styled similar to labor with discount breakdown
      if (item.parts && item.parts.length > 0) {
        pdf.setFontSize(9);
        pdf.setTextColor(colors.text);

        const partsTotal = item.parts.reduce((sum, part) => sum + (part.quantity * part.unit_price), 0);
        const hasPartsDiscounts = item.parts.some(part => part.discount_percentage || part.discount_amount);

        if (hasPartsDiscounts && options.showDiscountBreakdown) {
          // Show original parts cost
          pdf.text(`Parts: ${item.parts.length} item${item.parts.length !== 1 ? 's' : ''} @ ${this.formatCurrency(partsTotal)}`, rateX, rateY);
          rateY += 3;

          // Calculate total parts discount
          const partsDiscountAmount = item.parts.reduce((sum, part) => {
            return sum + (part.discount_amount ||
              (part.quantity * part.unit_price * (part.discount_percentage || 0) / 100));
          }, 0);

          // Show discount info
          pdf.setFontSize(8);
          pdf.setTextColor(200, 0, 0);
          pdf.text(`  Discount: -${this.formatCurrency(partsDiscountAmount)}`, rateX, rateY);
          rateY += 3;

          // Show final parts cost
          const finalPartsTotal = partsTotal - partsDiscountAmount;
          pdf.setFontSize(9);
          pdf.setTextColor(0, 150, 0);
          pdf.text(`  Final: ${this.formatCurrency(finalPartsTotal)}`, rateX, rateY);
          pdf.setTextColor(colors.text);
        } else {
          pdf.text(`Parts: ${item.parts.length} item${item.parts.length !== 1 ? 's' : ''} @ ${this.formatCurrency(partsTotal)}`, rateX, rateY);
        }
        rateY += 4;
      } else if (!item.labor_hours) {
        pdf.setFontSize(9);
        pdf.setTextColor(colors.text);
        pdf.text('Service item', rateX, rateY);
        rateY += 4;
      }

      // Discount column (if enabled)
      let nextColumnIndex = 3;
      if (hasAnyLineItemDiscounts) {
        const discountX = startX + colWidths[0] + colWidths[1] + colWidths[2] + 3;

        const laborDiscountAmount = item.labor_discount_amount ||
          (item.labor_hours && item.hourly_rate && item.labor_discount_percentage ?
            (item.labor_hours * item.hourly_rate * item.labor_discount_percentage / 100) : 0);

        const partDiscountAmount = item.parts ? item.parts.reduce((sum, part) => {
          return sum + (part.discount_amount ||
            (part.quantity * part.unit_price * (part.discount_percentage || 0) / 100));
        }, 0) : 0;

        const totalDiscountAmount = laborDiscountAmount + partDiscountAmount;

        pdf.setFontSize(fonts.body);
        pdf.setTextColor(colors.text);

        const discountY = yPosition + (rowHeight / 2) + 2; // Center vertically
        if (totalDiscountAmount > 0) {
          pdf.setTextColor(200, 0, 0);
          pdf.text(`-${this.formatCurrency(totalDiscountAmount).substring(1)}`, discountX + colWidths[3] - 3, discountY, { align: 'right' });
        } else {
          pdf.text('—', discountX + colWidths[3] - 3, discountY, { align: 'right' });
        }

        nextColumnIndex = 4;
      }

      // Amount column
      const amountX = startX + colWidths.slice(0, nextColumnIndex).reduce((sum, width) => sum + width, 0);
      let amountY = yPosition + 6;

      const hasDiscounts = (item.labor_discount_percentage || item.labor_discount_amount) ||
                          (item.parts && item.parts.some(part => part.discount_percentage || part.discount_amount));

      if (hasDiscounts && options.showOriginalPrices) {
        // Calculate original total
        let originalTotal = 0;
        if (item.labor_hours && item.hourly_rate) {
          originalTotal += item.labor_hours * item.hourly_rate;
        }
        if (item.parts) {
          originalTotal += item.parts.reduce((sum, part) => sum + (part.quantity * part.unit_price), 0);
        }

        // Show original price crossed out
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        const originalText = this.formatCurrency(originalTotal);
        pdf.text(originalText, amountX + colWidths[nextColumnIndex] - 3, amountY, { align: 'right' });

        // Draw strikethrough line
        const textWidth = pdf.getTextWidth(originalText);
        pdf.setDrawColor(150, 150, 150);
        pdf.setLineWidth(0.3);
        pdf.line(amountX + colWidths[nextColumnIndex] - 3 - textWidth, amountY - 1, amountX + colWidths[nextColumnIndex] - 3, amountY - 1);

        amountY += 4;

        // Show discounted price
        pdf.setFontSize(fonts.body);
        pdf.setTextColor(0, 150, 0);
        pdf.setFont(undefined, 'bold');
        pdf.text(this.formatCurrency(item.total_price || 0), amountX + colWidths[nextColumnIndex] - 3, amountY, { align: 'right' });

        amountY += 4;

        // Show savings
        const savings = originalTotal - (item.total_price || 0);
        if (savings > 0) {
          pdf.setFontSize(7);
          pdf.setTextColor(200, 0, 0);
          pdf.setFont(undefined, 'normal');
          pdf.text(`You save ${this.formatCurrency(savings)}`, amountX + colWidths[nextColumnIndex] - 3, amountY, { align: 'right' });
        }
      } else {
        // No discounts - show regular price (centered vertically)
        pdf.setFontSize(fonts.body);
        pdf.setTextColor(colors.text);
        pdf.setFont(undefined, 'bold');
        const simpleAmountY = yPosition + (rowHeight / 2) + 2; // Center vertically
        pdf.text(this.formatCurrency(item.total_price || 0), amountX + colWidths[nextColumnIndex] - 3, simpleAmountY, { align: 'right' });
      }

      yPosition += rowHeight;

      // Add professional parts table if enabled
      if (options.showPartDetails && item.parts && item.parts.length > 0) {
        yPosition += 5;

        // Parts table header
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.setFont(undefined, 'bold');
        pdf.text('Parts Breakdown:', startX + 5, yPosition);
        yPosition += 3;

        // Check if any parts have discounts to determine table structure
        const hasAnyPartDiscounts = item.parts.some(part =>
          part.discount_percentage || part.discount_amount
        );

        // Parts table structure - conditional discount column
        const partsTableHeaders = hasAnyPartDiscounts ?
          ['Part #', 'Description', 'Qty', 'Unit Price', 'Discount', 'Total'] :
          ['Part #', 'Description', 'Qty', 'Unit Price', 'Total'];
        const partsColWidths = hasAnyPartDiscounts ?
          [25, 45, 12, 20, 18, 20] :
          [25, 50, 15, 25, 25];
        const partsTableWidth = partsColWidths.reduce((sum, width) => sum + width, 0);
        const partsStartX = startX + 8;

        // Parts table header background
        pdf.setFillColor(240, 240, 240);
        pdf.rect(partsStartX, yPosition, partsTableWidth, 6, 'F');

        // Parts table header borders
        if (options.borderStyle !== 'none') {
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.1);
          pdf.rect(partsStartX, yPosition, partsTableWidth, 6);
        }

        // Parts header text
        pdf.setFontSize(7);
        pdf.setTextColor(60, 60, 60);
        pdf.setFont(undefined, 'bold');

        let partsXPos = partsStartX + 1;
        partsTableHeaders.forEach((header, index) => {
          const textAlign = index === 0 ? 'left' : index >= 3 ? 'right' : 'center';
          const textX = textAlign === 'right' ? partsXPos + partsColWidths[index] - 1 :
                       textAlign === 'center' ? partsXPos + partsColWidths[index] / 2 : partsXPos;

          pdf.text(header, textX, yPosition + 4, { align: textAlign });
          partsXPos += partsColWidths[index];
        });

        yPosition += 6;
        pdf.setFont(undefined, 'normal');

        // Parts table rows
        item.parts.forEach((part, partIndex) => {
          const basePrice = part.quantity * part.unit_price;
          const finalPrice = part.discounted_price ?? part.total_price;
          const hasPartDiscount = part.discount_percentage || part.discount_amount;
          const partRowHeight = 5;

          // Alternating row colors
          if (partIndex % 2 === 1) {
            pdf.setFillColor(248, 248, 248);
            pdf.rect(partsStartX, yPosition, partsTableWidth, partRowHeight, 'F');
          }

          // Row borders
          if (options.borderStyle !== 'none') {
            pdf.setDrawColor(220, 220, 220);
            pdf.setLineWidth(0.1);
            pdf.rect(partsStartX, yPosition, partsTableWidth, partRowHeight);

            // Column separators
            let sepX = partsStartX;
            partsColWidths.forEach((width, idx) => {
              if (idx < partsColWidths.length - 1) {
                sepX += width;
                pdf.line(sepX, yPosition, sepX, yPosition + partRowHeight);
              }
            });
          }

          // Part data
          pdf.setFontSize(7);
          pdf.setTextColor(40, 40, 40);

          let partXPos = partsStartX + 1;

          // Part Number
          pdf.text(part.part_number, partXPos, yPosition + 3);
          partXPos += partsColWidths[0];

          // Description (truncated if too long)
          const maxDescLength = 20;
          const partDesc = part.description.length > maxDescLength ?
            part.description.substring(0, maxDescLength) + '...' : part.description;
          pdf.text(partDesc, partXPos, yPosition + 3);
          partXPos += partsColWidths[1];

          // Quantity
          pdf.text(part.quantity.toString(), partXPos + partsColWidths[2]/2, yPosition + 3, { align: 'center' });
          partXPos += partsColWidths[2];

          // Unit Price
          pdf.text(this.formatCurrency(part.unit_price), partXPos + partsColWidths[3] - 1, yPosition + 3, { align: 'right' });
          partXPos += partsColWidths[3];

          // Discount (only if table has discount column)
          if (hasAnyPartDiscounts) {
            let discountText = '—';
            if (hasPartDiscount) {
              if (part.discount_percentage) {
                discountText = this.formatPercentage(part.discount_percentage);
              } else if (part.discount_amount) {
                discountText = this.formatCurrency(part.discount_amount);
              }
            }
            pdf.text(discountText, partXPos + partsColWidths[4] - 1, yPosition + 3, { align: 'right' });
            partXPos += partsColWidths[4];
          }

          // Total
          pdf.setFont(undefined, 'bold');
          if (hasPartDiscount) {
            pdf.setTextColor(0, 120, 0);
          }
          const totalColIndex = hasAnyPartDiscounts ? 5 : 4;
          pdf.text(this.formatCurrency(finalPrice), partXPos + partsColWidths[totalColIndex] - 1, yPosition + 3, { align: 'right' });
          pdf.setFont(undefined, 'normal');
          pdf.setTextColor(40, 40, 40);

          yPosition += partRowHeight;
        });

        // Close parts table border
        if (options.borderStyle !== 'none') {
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.1);
          pdf.line(partsStartX, yPosition, partsStartX + partsTableWidth, yPosition);
        }

        yPosition += 5;
      }
    });

    // Close table border
    if (options.borderStyle !== 'none') {
      pdf.setDrawColor(colors.primary);
      pdf.setLineWidth(0.5);
      pdf.line(startX, yPosition, startX + tableWidth, yPosition);
    }

    pdf.setFont(undefined, 'normal');
    return yPosition + 15;
  }

  private static addTotalSection(
    pdf: jsPDF,
    estimate: Estimate,
    colors: any,
    yPosition: number,
    pageWidth: number
  ): number {
    const rightAlign = pageWidth - 20;
    const labelX = rightAlign - 60;
    const valueX = rightAlign - 20;

    // Calculate total discounts
    const totalDiscounts = estimate.line_items.reduce((sum, item) => {
      return sum + this.calculateItemDiscounts(item);
    }, 0);

    // Check if we need a page break for totals section
    const totalsHeight = totalDiscounts > 0 ? 50 : 30;
    yPosition = this.checkPageBreak(pdf, yPosition, totalsHeight);

    // Show original subtotal if there are discounts
    if (totalDiscounts > 0) {
      const originalSubtotal = (estimate.subtotal || 0) + totalDiscounts;

      pdf.setFontSize(10);
      pdf.setTextColor(colors.text);
      pdf.text('Original Subtotal:', labelX, yPosition, { align: 'right' });
      pdf.text(this.formatCurrency(originalSubtotal), valueX, yPosition, { align: 'right' });
      yPosition += 6;

      // Total discounts
      pdf.setTextColor('#ef4444'); // Red color for discounts
      pdf.text('Total Discounts:', labelX, yPosition, { align: 'right' });
      pdf.text(`-${this.formatCurrency(totalDiscounts).substring(1)}`, valueX, yPosition, { align: 'right' });
      yPosition += 6;
    }

    // Subtotal
    pdf.setFontSize(10);
    pdf.setTextColor(colors.text);
    pdf.text('Subtotal:', labelX, yPosition, { align: 'right' });
    pdf.text(this.formatCurrency(estimate.subtotal || 0), valueX, yPosition, { align: 'right' });
    yPosition += 6;

    // Whole-job (header) discount — stored on the estimate; without this row
    // Subtotal + Tax would not reconcile with the discounted TOTAL below.
    const jobDiscount = (estimate as any).discount_amount || 0;
    if (jobDiscount > 0) {
      pdf.setTextColor('#ef4444');
      pdf.text('Discount:', labelX, yPosition, { align: 'right' });
      pdf.text(`-${this.formatCurrency(jobDiscount).substring(1)}`, valueX, yPosition, { align: 'right' });
      yPosition += 6;
      pdf.setTextColor(colors.text);
    }

    // Fees & surcharges — derived from the stored components so the printed
    // rows always sum to the stored TOTAL (no fetch needed).
    const feesTotal = feesFromStored(estimate as any);
    if (feesTotal > 0) {
      pdf.text('Fees & Surcharges:', labelX, yPosition, { align: 'right' });
      pdf.text(this.formatCurrency(feesTotal), valueX, yPosition, { align: 'right' });
      yPosition += 6;
    }

    // Tax
    pdf.text(`Tax (${this.formatPercentage(estimate.tax_rate || 0)}):`, labelX, yPosition, { align: 'right' });
    pdf.text(this.formatCurrency(estimate.tax_amount || 0), valueX, yPosition, { align: 'right' });
    yPosition += 6;

    // Total
    pdf.setFontSize(14);
    pdf.setTextColor(colors.primary);
    pdf.text('TOTAL:', labelX, yPosition, { align: 'right' });
    pdf.text(this.formatCurrency(estimate.total_amount || 0), valueX, yPosition, { align: 'right' });

    // Add savings note if there are discounts
    if (totalDiscounts > 0) {
      yPosition += 8;
      pdf.setFontSize(9);
      pdf.setTextColor('#10b981'); // Green color for savings
      pdf.text(`You saved ${this.formatCurrency(totalDiscounts)}!`, valueX, yPosition, { align: 'right' });
    }

    return yPosition + 15;
  }

  private static addInternalNotes(
    pdf: jsPDF,
    estimate: Estimate,
    colors: any,
    yPosition: number
  ): number {
    pdf.setFontSize(12);
    pdf.setTextColor(colors.primary);
    pdf.text('INTERNAL NOTES', 20, yPosition);
    yPosition += 8;

    pdf.setFontSize(10);
    pdf.setTextColor(colors.text);
    const notes = pdf.splitTextToSize(estimate.internal_notes || '', 170);
    pdf.text(notes, 20, yPosition);

    return yPosition + (notes.length * 5) + 10;
  }

  private static addDisclaimer(
    pdf: jsPDF,
    estimate: Estimate,
    colors: any,
    yPosition: number,
    pageWidth: number
  ): number {
    const pageHeight = pdf.internal.pageSize.getHeight();
    const footerSpace = 30; // Space needed for footer

    // Add proper spacing before disclaimer
    yPosition += 15;

    // Calculate disclaimer content
    const disclaimer = pdf.splitTextToSize(estimate.disclaimer || '', pageWidth - 40);
    const disclaimerHeight = 25 + (disclaimer.length * 4) + 15; // Header + content + spacing

    // Check if disclaimer will fit on current page with footer space
    if (yPosition + disclaimerHeight + footerSpace > pageHeight) {
      // Start disclaimer on new page
      pdf.addPage();
      yPosition = 30;
    }

    pdf.setFontSize(12);
    pdf.setTextColor(colors.primary);
    pdf.text('TERMS & CONDITIONS', 20, yPosition);
    yPosition += 10;

    pdf.setFontSize(9);
    pdf.setTextColor(colors.text);
    pdf.text(disclaimer, 20, yPosition);

    // Return position with space for footer
    return yPosition + (disclaimer.length * 4) + 15;
  }

  private static addFooter(
    pdf: jsPDF,
    companyInfo: CompanyInfo,
    colors: any,
    showPageNumbers: boolean = true
  ): void {
    const pageHeight = pdf.internal.pageSize.getHeight();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const totalPages = pdf.internal.getNumberOfPages();

    // Only add footer to the LAST page
    pdf.setPage(totalPages);

    const footerY = pageHeight - 15;

    // Footer background line
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.1);
    pdf.line(20, footerY - 5, pageWidth - 20, footerY - 5);

    pdf.setFontSize(8);
    pdf.setTextColor(colors.text);

    // Company info on left
    pdf.text(`${companyInfo.name} | ${companyInfo.phone} | ${companyInfo.email}`,
      20, footerY);

    // Page info on right
    if (showPageNumbers) {
      const pageText = totalPages > 1 ?
        `Generated on ${new Date().toLocaleDateString()} | Page ${totalPages} of ${totalPages}` :
        `Generated on ${new Date().toLocaleDateString()}`;

      pdf.text(pageText, pageWidth - 20, footerY, { align: 'right' });
    }
  }

  // Compact layout methods
  private static addCompactHeader(
    pdf: jsPDF,
    companyInfo: CompanyInfo,
    estimate: Estimate,
    colors: any,
    yPosition: number,
    pageWidth: number
  ): number {
    // Company name and estimate number on same line
    pdf.setFontSize(16);
    pdf.setTextColor(colors.primary);
    pdf.text(companyInfo.name, 20, yPosition);
    pdf.text(`ESTIMATE #${estimate.estimate_number}`, pageWidth - 20, yPosition, { align: 'right' });

    yPosition += 8;
    pdf.setFontSize(8);
    pdf.setTextColor(colors.text);
    pdf.text(`${companyInfo.phone} | ${companyInfo.email}`, 20, yPosition);
    pdf.text(new Date(estimate.created_at || '').toLocaleDateString(), pageWidth - 20, yPosition, { align: 'right' });

    return yPosition + 10;
  }

  private static addCompactCustomerVehicle(
    pdf: jsPDF,
    estimate: Estimate,
    colors: any,
    yPosition: number
  ): number {
    pdf.setFontSize(10);
    pdf.setTextColor(colors.text);

    if (estimate.customer) {
      const customerName = estimate.customer.company_name ||
        `${estimate.customer.first_name} ${estimate.customer.last_name}`;
      pdf.text(`Customer: ${customerName}`, 20, yPosition);
    }

    if (estimate.vehicle) {
      pdf.text(`Vehicle: ${estimate.vehicle.year} ${estimate.vehicle.make} ${estimate.vehicle.model}`,
        20, yPosition + 5);
    }

    return yPosition + 15;
  }

  private static addCompactLineItems(
    pdf: jsPDF,
    estimate: Estimate,
    options: PDFLayoutOptions,
    colors: any,
    yPosition: number,
    pageWidth: number
  ): number {
    pdf.setFontSize(10);
    pdf.setTextColor(colors.text);

    estimate.line_items.forEach((item) => {
      // Check page break
      yPosition = this.checkPageBreak(pdf, yPosition, 10);

      // Wrap description text
      const descLines = pdf.splitTextToSize(item.description, pageWidth - 100);
      pdf.text(descLines[0], 20, yPosition);
      pdf.text(this.formatCurrency(item.total_price || 0), pageWidth - 20, yPosition, { align: 'right' });
      yPosition += 5;
    });

    return yPosition + 5;
  }

  private static addCompactTotal(
    pdf: jsPDF,
    estimate: Estimate,
    colors: any,
    yPosition: number,
    pageWidth: number
  ): number {
    const rightAlign = pageWidth - 20;

    // Check page break for totals
    yPosition = this.checkPageBreak(pdf, yPosition, 20);

    pdf.setFontSize(12);
    pdf.setTextColor(colors.primary);
    pdf.text('TOTAL:', rightAlign - 40, yPosition, { align: 'right' });
    pdf.text(this.formatCurrency(estimate.total_amount || 0), rightAlign, yPosition, { align: 'right' });

    return yPosition + 15;
  }

  private static addCompactDisclaimer(
    pdf: jsPDF,
    estimate: Estimate,
    colors: any,
    yPosition: number
  ): void {
    pdf.setFontSize(7);
    pdf.setTextColor(colors.text);
    const disclaimer = pdf.splitTextToSize(estimate.disclaimer || '', 170);
    pdf.text(disclaimer, 20, yPosition);
  }

  private static calculateItemDiscounts(item: any): number {
    return this.calculateLaborDiscount(item) + this.calculatePartDiscounts(item);
  }

  private static calculateLaborDiscount(item: any): number {
    if (!item.labor_hours || !item.hourly_rate) return 0;

    const baseLaborCost = item.labor_hours * item.hourly_rate;
    const laborDiscountAmount = item.labor_discount_amount ||
      (baseLaborCost * (item.labor_discount_percentage || 0) / 100);
    return laborDiscountAmount;
  }

  private static calculatePartDiscounts(item: any): number {
    if (!item.parts) return 0;

    return item.parts.reduce((partSum: number, part: any) => {
      const basePartCost = part.quantity * part.unit_price;
      const partDiscountAmount = part.discount_amount ||
        (basePartCost * (part.discount_percentage || 0) / 100);
      return partSum + partDiscountAmount;
    }, 0);
  }
}